import { Check, Circle, Eye, EyeOff } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useParams } from "react-router-dom"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useAuthForm } from "@/features/auth/hooks/useAuthForm"
import type { GoogleSignupFormData, SignupFormData } from "@/features/auth/schemas/auth.schema"
import { googleSignupSchema } from "@/features/auth/schemas/auth.schema"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"

interface GoogleSignupData {
  name: string
  email: string
  username: string
}

interface AppleSignupData {
  name: string
  email: string
  username: string
}

function PasswordRequirementsTooltip({
  requirements,
  rightPos,
  belowPos,
  title,
}: {
  requirements: { label: string; met: boolean }[]
  rightPos: { top: number; left: number }
  belowPos: { top: number; left: number }
  title: string
}) {
  if (typeof document === "undefined") return null

  const content = (
    <div className="bg-[#1C1C1C] border border-dashed border-[#FFFFFF33] rounded-[20px] p-5 shadow-xl w-83.75">
      <p className="text-sm leading-6.25 font-semibold uppercase text-primary mb-2.5">{title}</p>
      <ul className="space-y-2.5">
        {requirements.map(req => (
          <li key={req.label} className="flex items-center gap-2.5">
            {req.met ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 1.6665C14.6024 1.6665 18.3334 5.39746 18.3334 9.99984C18.3334 14.6022 14.6024 18.3332 10 18.3332C5.39765 18.3332 1.66669 14.6022 1.66669 9.99984C1.66669 5.39746 5.39765 1.6665 10 1.6665ZM12.6831 7.47456L8.95835 11.1993L7.31696 9.5579C7.07288 9.31382 6.67716 9.31382 6.43308 9.5579C6.189 9.80197 6.189 10.1977 6.43308 10.4418L8.51641 12.5251C8.76049 12.7692 9.15622 12.7692 9.40029 12.5251L13.567 8.35845C13.811 8.11437 13.811 7.71864 13.567 7.47456C13.3229 7.23048 12.9272 7.23048 12.6831 7.47456Z"
                  fill="#5A5B3F"
                />
              </svg>
            ) : (
              <Circle size={20} className="text-[#5A5B3F] shrink-0" />
            )}
            <span className="text-[13px] leading-5 text-[#F2F0EBE6]">{req.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )

  return createPortal(
    <>
      <div
        className="hidden xl:block fixed z-50 transition-opacity duration-200"
        style={{ top: rightPos.top, left: rightPos.left }}
        data-password-tooltip
      >
        {content}
      </div>
      <div
        className="block xl:hidden fixed z-50 transition-opacity duration-200"
        style={{ top: belowPos.top, left: belowPos.left }}
        data-password-tooltip
      >
        {content}
      </div>
    </>,
    document.body,
  )
}

/**
 * SignupScreen - Registration with profile creation
 */
export function SignupScreen() {
  const navigate = useNavigate()
  const { username } = useParams<{ username: string }>()
  const { t } = useTranslation(["auth", "profile", "common", "onboarding"])
  const signup = useAuthStore(state => state.signup)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [rightPos, setRightPos] = useState({ top: 0, left: 0 })
  const [belowPos, setBelowPos] = useState({ top: 0, left: 0 })
  const [isGoogleSignup, setIsGoogleSignup] = useState(false)
  const [isAppleSignup, setIsAppleSignup] = useState(false)
  const [locationDisplay, setLocationDisplay] = useState("")
  const passwordSectionRef = useRef<HTMLDivElement>(null)

  const handleSignupSubmit = async (data: SignupFormData | GoogleSignupFormData) => {
    if (isGoogleSignup) {
      try {
        await signup({ ...data, googleVerified: true } as SignupFormData & {
          googleVerified: boolean
        })
        navigate("/onboarding/permissions")
      } catch (error) {
        console.error("[SignupPage] signup error:", error)
      }
    } else if (isAppleSignup) {
      try {
        await signup({ ...data, appleVerified: true } as SignupFormData & {
          appleVerified: boolean
        })
        navigate("/onboarding/permissions")
      } catch (error) {
        console.error("[SignupPage] signup error:", error)
      }
    } else {
      if (!data.password || data.password.length < 8) {
        form.setError("password", {
          type: "manual",
          message: "Password must be at least 8 characters",
        })
        return
      }
      await signup(data as SignupFormData)
      navigate("/verify-email", { state: { email: data.email } })
    }
  }

  const { form, isSubmitting, handleSubmit } = useAuthForm<GoogleSignupFormData>(
    googleSignupSchema,
    handleSignupSubmit as (data: GoogleSignupFormData) => Promise<void>,
  )

  // Check for Google or Apple signup data in sessionStorage
  useEffect(() => {
    const googleDataStr = sessionStorage.getItem("google_signup")
    if (googleDataStr) {
      try {
        const googleData: GoogleSignupData = JSON.parse(googleDataStr)
        form.reset({
          name: googleData.name || "",
          username: googleData.username || "",
          email: googleData.email || "",
          password: "",
        })
        setIsGoogleSignup(true)
        sessionStorage.removeItem("google_signup")
      } catch {
        sessionStorage.removeItem("google_signup")
      }
      return
    }

    const appleDataStr = sessionStorage.getItem("apple_signup")
    if (appleDataStr) {
      try {
        const appleData: AppleSignupData = JSON.parse(appleDataStr)
        form.reset({
          name: appleData.name || "",
          username: appleData.username || "",
          email: appleData.email || "",
          password: "",
        })
        setIsAppleSignup(true)
        sessionStorage.removeItem("apple_signup")
      } catch {
        sessionStorage.removeItem("apple_signup")
      }
    }
  }, [form])

  const password = form.watch("password") || ""
  const confirmPassword = form.watch("confirmPassword") || ""

  const requirements = useMemo(
    () => [
      {
        label: t("auth:passwordRequirements.minLength"),
        met: password.length >= 8,
      },
      {
        label: t("auth:passwordRequirements.uppercaseLowercase"),
        met: /[a-z]/.test(password) && /[A-Z]/.test(password),
      },
      {
        label: t("auth:passwordRequirements.number"),
        met: /\d/.test(password),
      },
      {
        label: t("auth:passwordRequirements.specialCharacter"),
        met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password),
      },
      {
        label: t("auth:passwordRequirements.passwordsMatch"),
        met: password.length > 0 && confirmPassword.length > 0 && password === confirmPassword,
      },
    ],
    [password, confirmPassword, t],
  )

  const updateTooltipPosition = useCallback(() => {
    if (passwordSectionRef.current) {
      const sectionRect = passwordSectionRef.current.getBoundingClientRect()
      const card = passwordSectionRef.current.closest('[style*="max-width"]')
      const cardRect = card?.getBoundingClientRect() ?? sectionRect
      setRightPos({ top: sectionRect.top, left: cardRect.right + 14 })
      setBelowPos({ top: sectionRect.bottom + 8, left: sectionRect.left })
    }
  }, [])

  useEffect(() => {
    if (username) {
      form.setValue("referralCode", username.toUpperCase())
    }
  }, [username, form])

  useEffect(() => {
    if (!passwordFocused) return

    updateTooltipPosition()

    const handleClickOutside = (e: MouseEvent) => {
      if (passwordSectionRef.current && !passwordSectionRef.current.contains(e.target as Node)) {
        const tooltipEl = document.querySelector("[data-password-tooltip]")
        if (tooltipEl?.contains(e.target as Node)) return
        setPasswordFocused(false)
      }
    }
    const handleFocusOutside = (e: FocusEvent) => {
      if (passwordSectionRef.current && !passwordSectionRef.current.contains(e.target as Node)) {
        setPasswordFocused(false)
      }
    }
    const handleLayoutChange = () => updateTooltipPosition()

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("focusin", handleFocusOutside)
    window.addEventListener("resize", handleLayoutChange)
    window.addEventListener("scroll", handleLayoutChange, true)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("focusin", handleFocusOutside)
      window.removeEventListener("resize", handleLayoutChange)
      window.removeEventListener("scroll", handleLayoutChange, true)
    }
  }, [passwordFocused, updateTooltipPosition])

  return (
    <>
      <AuthWrapper className="mt-4 mb-20">
        {/* Title */}
        <div className="text-center mb-7 mt-4">
          <h2 className="text-[24px] font-monteserat font-semibold text-primary px-25 loading-0">
            {t("createProfile.title", { ns: "onboarding" })}
          </h2>
        </div>

        <div className="space-y-6 sm:px-5.5">
          {/* Form Fields */}
          <form className="space-y-4 px-5 sm:px-10 md:px-22">
            {/* Name Field */}
            <CustomInput
              label={t("signup.nameLabel")}
              type="text"
              placeholder={t("signup.namePlaceholder")}
              required={true}
              {...form.register("name")}
              error={form.formState.errors.name?.message as string | undefined}
              disabled={isSubmitting}
              onClearError={() => form.clearErrors("name")}
            />

            {/* Username Field */}
            <CustomInput
              label={t("signup.usernameLabel")}
              placeholder={t("signup.usernamePlaceholder")}
              type="text"
              required={true}
              {...form.register("username")}
              error={form.formState.errors.username?.message as string | undefined}
              disabled={isSubmitting}
              onClearError={() => form.clearErrors("username")}
            />

            {/* Location Field with Google Places Autocomplete */}
            <AutocompleteInput
              label={t("signup.cityLabel")}
              value={locationDisplay}
              placeholder={t("signup.cityPlaceholder")}
              onLocationSelect={locationData => {
                setLocationDisplay(locationData.address)
                form.setValue("city", locationData.city || "")
                form.setValue("state", locationData.state || "")
                form.setValue("country", locationData.country || "")
                form.setValue("zipcode", locationData.zipCode || "")
                form.setValue("latitude", locationData.lat)
                form.setValue("longitude", locationData.lng)
                form.clearErrors("city")
              }}
              error={form.formState.errors.city?.message as string | undefined}
              onClearError={() => form.clearErrors("city")}
              disabled={isSubmitting}
              required={true}
            />

            {/* Email Field */}
            <CustomInput
              label={t("signup.emailLabel")}
              type="email"
              placeholder={t("signup.emailPlaceholder")}
              required={true}
              {...form.register("email")}
              error={form.formState.errors.email?.message as string | undefined}
              disabled={isSubmitting || isGoogleSignup || isAppleSignup}
              readOnly={isGoogleSignup || isAppleSignup}
              onClearError={() => form.clearErrors("email")}
            />

            {/* Password Field - Hidden for Google and Apple signup */}
            {!isGoogleSignup && !isAppleSignup && (
              <div className="relative" ref={passwordSectionRef}>
                <div className="relative">
                  <CustomInput
                    label={t("signup.passwordLabel")}
                    placeholder={t("signup.passwordPlaceholder")}
                    type={showPassword ? "text" : "password"}
                    required={true}
                    {...form.register("password")}
                    error={form.formState.errors.password?.message as string | undefined}
                    disabled={isSubmitting}
                    onClearError={() => form.clearErrors("password")}
                    className="pr-8"
                    onFocus={() => setPasswordFocused(true)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-1 top-[70%] -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>

                {/* Confirm Password Field */}
                <div className="relative mt-4">
                  <CustomInput
                    label={t("signup.confirmPasswordLabel")}
                    placeholder={t("signup.confirmPasswordPlaceholder")}
                    type={showConfirmPassword ? "text" : "password"}
                    required={true}
                    {...form.register("confirmPassword")}
                    error={form.formState.errors.confirmPassword?.message as string | undefined}
                    disabled={isSubmitting}
                    onClearError={() => form.clearErrors("confirmPassword")}
                    className="pr-8"
                    onFocus={() => setPasswordFocused(true)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-1 top-[70%] -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
            )}

            {/* Referral Code Field */}
            <div className="relative">
              <CustomInput
                label={t("signup.referralLabel")}
                placeholder={t("signup.referralCodePlaceholder")}
                type="text"
                required={false}
                {...form.register("referralCode")}
                error={form.formState.errors.referralCode?.message as string | undefined}
                disabled={isSubmitting}
                onClearError={() => form.clearErrors("referralCode")}
                className="pr-8"
              />
              {form.watch("referralCode") && (
                <Check size={17} className="absolute right-1 top-1/2 -translate-y-1/2 text-white" />
              )}
            </div>
          </form>

          <div className="flex items-center justify-center mt-9 mb-2">
            {/* Continue Button */}
            <div className="w-full sm:w-auto">
              <Button
                type="button"
                onClick={handleSubmit}
                variant="primary-white"
                size="md"
                className="uppercase w-[362px] hover:!bg-[#F2F0EB]"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                fullWidth
              >
                {t("buttons.continue", { ns: "common" })}
              </Button>

              <p className="text-[14px] font-semibold text-center pt-7">
                {t("signup.alreadyHaveAccount")}
              </p>

              <Button
                type="button"
                onClick={() => navigate("/login")}
                variant="outline"
                size="md"
                className="uppercase w-[362px] mt-4.5 hover:!bg-transparent hover:!text-[#F2F0EB] hover:!border-[#F2F0EB]"
                fullWidth
              >
                {t("signup.loginLink")}
              </Button>
            </div>
          </div>
        </div>
      </AuthWrapper>

      {passwordFocused && (
        <PasswordRequirementsTooltip
          requirements={requirements}
          rightPos={rightPos}
          belowPos={belowPos}
          title={t("auth:passwordRequirements.title")}
        />
      )}
    </>
  )
}
